import { useMutation } from "@apollo/client";
import { XIcon } from "@heroicons/react/solid";
import topicDiscussed from "@static/img/topic-discussed.svg";
import topicUndiscussed from "@static/img/topic-undiscussed.svg";
import moment from "moment";
import { KeyboardEvent, useState } from "react";
import { TopicState } from "types/graphql-schema";
import type { GetProfileOneononeQueryQuery } from "types/graphql-schema";

import createOrUpdateTopicMutation from "@apps/meeting-new/graphql/create-or-update-topic-mutation";
import deleteTopicMutation from "@apps/topic-dropdown-menu/graphql/delete-topic-mutation";
import useLabel from "@apps/use-label/use-label";
import addTopicToMeeting from "@cache/add-topic-to-meeting";
import removeTopic from "@cache/remove-topic";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { assertEdgesNonNull, getAssetUrl, getUrl } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

type NextOneonone = NonNullable<
  NonNullable<
    NonNullable<GetProfileOneononeQueryQuery["nextOneonone"]>["edges"][0]
  >["node"]
>;

const ProfileNextOneononeCard = ({
  meeting,
  profileId,
}: {
  meeting: NextOneonone;
  profileId: number;
}) => {
  const label = useLabel();
  const [topicTitle, setTopicTitle] = useState("");
  const [createTopic] = useMutation(createOrUpdateTopicMutation);
  const [deleteTopic] = useMutation(deleteTopicMutation);

  const topics = meeting.topics ? assertEdgesNonNull(meeting.topics) : [];
  const meetingDate = meeting.startDatetime
    ? moment(meeting.startDatetime).format("ddd, MMM D")
    : "";
  const meetingTitle = meeting.title || label("1-on-1");

  const handleAddTopic = async () => {
    const title = topicTitle.trim();
    if (!title) return;

    setTopicTitle("");
    const { data } = await createTopic({
      variables: {
        meetingId: meeting.id,
        title,
      },
      update: (_cache, { data }) => {
        if (data?.createOrUpdateTopic?.topic) {
          addTopicToMeeting({
            topic: data.createOrUpdateTopic.topic,
            meeting,
            afterTopic: null,
            beforeTopic: null,
          });
        }
      },
    });
    return data;
  };

  const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const data = await handleAddTopic();
      if (!e.metaKey) {
        // Regular Enter: add topic and blur
        (e.target as HTMLInputElement).blur();
      }
      // Cmd+Enter: add topic and keep focus for adding another
      return data;
    }
  };

  const { confirm, ConfirmationDialog } = useConfirm(
    "Confirm",
    "Are you sure you want to delete this topic?",
  );
  const handleDeleteTopic = async (topicId: number) => {
    const confirmed = await confirm();
    if (confirmed) {
      await deleteTopic({
        variables: { topicId },
        update: () => {
          removeTopic({
            topic: { id: topicId },
            meeting,
          });
        },
      });
    }
  };

  const meetingUrl = getUrl({
    meetingGroupId: meeting.meetingGroupId,
    meetingId: meeting.id,
  });

  const viewAllUrl = `/meetings?userId=${profileId}&oneonone=true`;

  return (
    <div className="flex flex-col gap-6 border rounded-md p-6">
      <ConfirmationDialog />
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base text-gray-900">
            Next {label("1-on-1", { titleCase: true })}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
            <span className="whitespace-nowrap">{meetingDate}</span>
            <span className="text-gray-300">•</span>
            <span className="truncate">{meetingTitle}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button theme={buttonTheme.default} to={viewAllUrl}>
            <TopicflowIcon type="meeting" className="w-4 h-4" />
            View all {label("1-on-1")}s
          </Button>
          <Button theme={buttonTheme.primary} to={meetingUrl}>
            <TopicflowIcon type="meeting" className="w-4 h-4" />
            Open {label("1-on-1")}
          </Button>
        </div>
      </div>

      {/* Topics section */}
      <div className="flex flex-col gap-2">
        <div className="font-semibold text-sm text-gray-800">Topics</div>

        {topics.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {topics.map((topic) => (
              <div key={topic.id} className="flex items-start gap-2 group">
                <div className="mt-0.5 shrink-0">
                  {topic.state === TopicState.ClosedToDiscussion ? (
                    <img
                      src={getAssetUrl(topicDiscussed)}
                      alt="Discussed topic"
                      className="w-4 h-4"
                    />
                  ) : (
                    <img
                      src={getAssetUrl(topicUndiscussed)}
                      alt="Undiscussed topic"
                      className="w-4 h-4"
                    />
                  )}
                </div>
                <div className="flex-1 text-sm text-gray-600 min-w-0">
                  {topic.title}
                </div>
                {topic.state !== TopicState.ClosedToDiscussion &&
                  topics.length > 1 &&
                  topic.canDelete?.permission && (
                    <button
                      onClick={() => handleDeleteTopic(topic.id)}
                      className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  )}
              </div>
            ))}
          </div>
        )}

        <input
          type="text"
          value={topicTitle}
          onChange={(e) => setTopicTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a topic title, Enter to add another"
          className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  );
};

export default ProfileNextOneononeCard;
